import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckSquare, ExternalLink } from 'lucide-react';
import { SEO } from '../../../components/SEO';

const TICKED_TEAL = '#0D9488';

/* â”€â”€â”€ Shared sub-components â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const TickedTopBar = () => (
  <div
    className="sticky top-0 z-50 border-b border-[#E2E8F0]"
    style={{ background: 'rgba(248,247,244,0.95)', backdropFilter: 'blur(20px)' }}
  >
    <div className="container-custom py-4 flex items-center justify-between">
      <Link
        to="/apps/ticked"
        className="flex items-center gap-2 text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors group"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
        Back to Ticked
      </Link>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center rounded-sm" style={{ backgroundColor: TICKED_TEAL }}>
          <CheckSquare size={15} color="#fff" />
        </div>
        <span className="font-display text-[1.2rem] font-bold text-[#0F172A]">Ticked</span>
      </div>
      <a
        href="#"
        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: '#95BF47' }}
      >
        Add to Shopify
        <ExternalLink size={12} />
      </a>
    </div>
  </div>
);

const TickedFooter = () => (
  <div className="border-t border-[#E2E8F0] py-6">
    <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] uppercase">
        © {new Date().getFullYear()} Ticked · A Triple A Solutions Product
      </p>
      <div className="flex items-center gap-4">
        <Link to="/apps/ticked/terms" className="font-mono text-[0.55rem] tracking-widest text-[#0D9488] uppercase">Terms</Link>
        <span className="text-[#E2E8F0]">·</span>
        <Link to="/apps/ticked/privacy" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Privacy</Link>
      </div>
    </div>
  </div>
);

const Section = ({ num, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
  >
    <div className="flex items-start gap-4 mt-12 mb-3">
      <span className="font-mono text-[0.6rem] tracking-widest uppercase shrink-0 mt-1.5" style={{ color: TICKED_TEAL }}>{num}</span>
      <h2 className="font-display text-xl font-bold text-[#0F172A]">{title}</h2>
    </div>
    <div className="pl-8">{children}</div>
  </motion.div>
);

const P = ({ children }) => (
  <p className="text-[#374151] text-sm leading-relaxed mb-3">{children}</p>
);

/* â”€â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export const TickedTermsPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4] flex flex-col">
      <SEO
        title="Ticked Terms & Conditions"
        description="Terms and conditions for the Ticked Shopify app by Smart Solutions."
        path="/apps/ticked/terms"
      />
      <TickedTopBar />

      <div className="flex-1">
        <div className="container-custom py-16 max-w-3xl mx-auto">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label">Legal</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h1
              className="font-display font-bold text-[#0F172A] mb-3"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
            >
              Terms &amp; Conditions
            </h1>
            <p className="font-mono text-[0.65rem] tracking-widest text-[#9CA3AF] uppercase mb-8">
              Last updated: March 21, 2026
            </p>
            <div className="divider-gold opacity-40" />
          </motion.div>

          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-8"
          >
            <P>
              Please read these Terms and Conditions carefully before installing or using Ticked. By installing the
              app from the Shopify App Store, you confirm that you have read, understood, and agree to be bound by
              these Terms. These Terms form a legal agreement between you (the Merchant) and Triple A Solutions,
              the operator of Ticked.
            </P>
          </motion.div>

          <Section num="01" title="Acceptance of Terms">
            <P>
              By installing or using Ticked ("the App"), you ("Merchant") agree to these Terms and Conditions. If
              you do not agree, you must not install or use the App. These Terms apply to all users of the App,
              including merchant account holders and any staff members given access.
            </P>
          </Section>

          <Section num="02" title="Description of Service">
            <P>
              Ticked is a task management application embedded within the Shopify admin. It allows merchants and
              their staff to create, assign, and manage tasks; track work progress; collaborate via comments and
              checklists; and link tasks to Shopify orders.
            </P>
            <P>
              Staff members are given PIN-based access configured by the merchant. The App is hosted on Microsoft
              Azure cloud infrastructure and delivered as a Software-as-a-Service (SaaS) product installed via the
              Shopify App Store.
            </P>
          </Section>

          <Section num="03" title="Merchant Responsibilities">
            <P>
              As the merchant, you are responsible for managing your own team members within the App, including
              creating staff profiles, assigning roles, and keeping PIN codes secure. You must ensure that staff
              access is promptly revoked for any team member who leaves your organisation or should no longer have
              access.
            </P>
            <P>
              You are responsible for the accuracy of data entered into the App, including task details, staff
              information, and any linked Shopify order references. You must ensure that your use of the App
              complies with applicable laws and Shopify's Partner policies.
            </P>
          </Section>

          <Section num="04" title="Acceptable Use Policy">
            <P>
              You agree not to use the App for any unlawful, harmful, or abusive purpose. You must not attempt to
              reverse-engineer, decompile, or otherwise extract the source code of the App. You must not use the
              App in a way that could damage, disable, overburden, or impair our infrastructure or interfere with
              any other user's access to the App.
            </P>
            <P>
              You must not use the App to store, transmit, or process any content that is illegal, defamatory,
              harassing, or that violates any third-party rights.
            </P>
          </Section>

          <Section num="05" title="Intellectual Property">
            <P>
              All intellectual property rights in the App — including but not limited to the software, user
              interface, design, and documentation — are owned by Triple A Solutions or its licensors. These Terms
              do not grant you any rights in or to the App beyond the limited right to use it in accordance with
              these Terms.
            </P>
            <P>
              You retain all ownership rights in any data you enter into the App, including task content, staff
              profiles, and notes.
            </P>
          </Section>

          <Section num="06" title="Data and Privacy">
            <P>
              The App collects and processes certain data in order to provide its functionality, including staff
              profile data, task data, comments, and audit logs. Full details of what data we collect, how we use
              it, and your rights are described in our{' '}
              <Link to="/apps/ticked/privacy" style={{ color: TICKED_TEAL }} className="hover:underline">
                Privacy Policy
              </Link>
              , which forms part of these Terms.
            </P>
            <P>
              By using the App, you acknowledge that you have read and agree to our Privacy Policy.
            </P>
          </Section>

          <Section num="07" title="Limitation of Liability">
            <P>
              To the fullest extent permitted by applicable law, Triple A Solutions and its affiliates, directors,
              and employees shall not be liable for any indirect, incidental, special, consequential, or punitive
              damages arising out of or in connection with your use of the App, including but not limited to loss
              of data, loss of revenue, or business interruption.
            </P>
            <P>
              Our total aggregate liability to you under these Terms shall not exceed the amount paid by you for
              the App in the three months preceding the event giving rise to the claim.
            </P>
          </Section>

          <Section num="08" title="Indemnification">
            <P>
              You agree to indemnify, defend, and hold harmless Triple A Solutions and its affiliates from and
              against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees)
              arising out of or in connection with your use of the App, your breach of these Terms, or your
              violation of any applicable law or third-party right.
            </P>
          </Section>

          <Section num="09" title="Modifications to the Service or Terms">
            <P>
              We reserve the right to update, modify, or discontinue the App or any part of it at any time, with
              or without notice. We may also update these Terms from time to time. If we make material changes,
              we will notify you via the Shopify admin or by email.
            </P>
            <P>
              Continued use of the App after changes to these Terms take effect constitutes your acceptance of the
              revised Terms.
            </P>
          </Section>

          <Section num="10" title="Termination">
            <P>
              You may terminate your use of the App at any time by uninstalling it from your Shopify store.
              Upon uninstallation, access to the App will cease immediately.
            </P>
            <P>
              <strong className="font-medium text-[#0F172A]">Data retention:</strong> [DATA RETENTION / DELETION POLICY — to be filled.
              Example: Your data will be retained for 30 days after uninstallation and then permanently deleted,
              unless you request earlier deletion by contacting us.]
            </P>
            <P>
              We reserve the right to suspend or terminate your access to the App at any time if we reasonably
              believe you have breached these Terms.
            </P>
          </Section>

          <Section num="11" title="Contact Information">
            <P>
              If you have any questions about these Terms, please contact us:
            </P>
            <div className="bg-white border border-[#E2E8F0] p-5 mt-2" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <p className="text-sm font-medium text-[#0F172A]">Smart Solutions</p>
              <p className="text-sm text-[#374151] mt-1">
                <a href="mailto:hello@smartsolution.ai" style={{ color: TICKED_TEAL }} className="hover:underline">
                  hello@smartsolution.ai
                </a>
              </p>
              <p className="text-sm text-[#374151]">Kuwait City, Kuwait</p>
            </div>
          </Section>

          <div className="divider-gold opacity-30 mt-16" />
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6">
            <p className="font-mono text-[0.6rem] tracking-widest text-[#9CA3AF] uppercase">
              Last updated: March 21, 2026
            </p>
            <Link
              to="/apps/ticked/privacy"
              className="font-mono text-[0.6rem] tracking-widest hover:underline uppercase"
              style={{ color: TICKED_TEAL }}
            >
              View Privacy Policy →
            </Link>
          </div>
        </div>
      </div>

      <TickedFooter />
    </div>
  );
};
